/**
 * KLYROO — centralized analytics (GA4 via gtag.js) + Google Ads conversion foundation.
 *
 * ONE authoritative Google tag. Env-driven. No hard-coded IDs. Safe no-op when unconfigured.
 *  - REACT_APP_GA4_MEASUREMENT_ID        e.g. G-XXXXXXXXXX  (public, frontend-safe)
 *  - REACT_APP_GOOGLE_ADS_CONVERSION_ID  e.g. AW-XXXXXXXXX  (optional, enables Ads conversions)
 *  - REACT_APP_ADS_LABEL_<KEY>           conversion labels, provided later per action
 *
 * Consent Mode v2 is DENIED by default. The KLYROO Consent Banner (components/ConsentBanner.jsx)
 * is the mechanism that collects a real choice and calls window.klyrooConsent.update(...). See ANALYTICS.md.
 *
 * Privacy: sanitize() strips PII before anything reaches GA4. Never pass email/name/phone/
 * message/suggestion text/admin notes/IP/tokens as event params.
 */

const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;

// Logical conversion -> Google Ads conversion label (env-provided later). No labels => no-op.
const CONV_LABELS = {
  suggestion: process.env.REACT_APP_ADS_LABEL_SUGGESTION,
  advertising: process.env.REACT_APP_ADS_LABEL_ADVERTISING,
  newsletter: process.env.REACT_APP_ADS_LABEL_NEWSLETTER,
};

let initialized = false;

export function isEnabled() {
  return !!GA_ID;
}

export function initAnalytics() {
  if (initialized || !GA_ID || typeof window === "undefined") return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  // Consent Mode v2 — DENIED by default. Must be set BEFORE config.
  // A future consent banner/CMP flips these via window.klyrooConsent.update(...).
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });
  // Preserve Google Ads attribution across navigation while cookies are denied,
  // and redact ads data while ad_storage is denied.
  gtag("set", "url_passthrough", true);
  gtag("set", "ads_data_redaction", true);

  // Load gtag.js exactly once.
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  gtag("js", new Date());
  // SPA: disable automatic page_view — we send it manually on each route change.
  gtag("config", GA_ID, { send_page_view: false });
  // Google Ads: only configured when a real conversion ID is provided.
  if (ADS_ID) gtag("config", ADS_ID);
}

/* ---------------- PII safeguards ---------------- */
const BLOCK_EXACT = new Set([
  "name", "ip", "email", "phone", "fname", "lname", "firstname", "lastname",
  "first_name", "last_name", "full_name", "username", "user_name", "user_ip",
  "ip_address", "address", "password", "passwd", "message", "suggestion",
  "reason", "notes", "admin_notes", "inquiry", "contact_name", "contact",
]);
const BLOCK_SUBSTR = ["email", "phone", "password", "passwd", "secret", "token", "apikey", "api_key", "credential", "admin_note"];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function sanitize(params = {}) {
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    const kl = String(k).toLowerCase();
    if (BLOCK_EXACT.has(kl)) continue;
    if (BLOCK_SUBSTR.some((s) => kl.includes(s))) continue;
    if (typeof v === "string") {
      if (v.length > 300) continue;          // no long free text
      if (EMAIL_RE.test(v.trim())) continue;  // never leak an email by value
    }
    out[k] = v;
  }
  return out;
}

/* ---------------- Core senders ---------------- */
export function trackEvent(name, params = {}) {
  beaconHealth(name);
  if (!GA_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, sanitize(params));
}

export function trackPageView(path, title) {
  beaconHealth("page_view");
  if (!GA_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href, // full href preserves gclid/gbraid/wbraid
    page_title: title || document.title,
    page_referrer: document.referrer || undefined,
  });
}

/* ---------------- Consent update mechanism (for a FUTURE banner/CMP) ---------------- */
export function updateConsent(consent) {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("consent", "update", consent);
}
export function grantAllConsent() {
  updateConsent({
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
}
export function denyAllConsent() {
  updateConsent({
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
}
if (typeof window !== "undefined") {
  // Public hook the KLYROO Consent Banner (and any future CMP) calls to update consent.
  window.klyrooConsent = { update: updateConsent, grantAll: grantAllConsent, denyAll: denyAllConsent };
}

/* ---------------- Google Ads conversion hook (safe no-op until configured) ---------------- */
export function markConversion(key, params = {}) {
  if (!ADS_ID || typeof window === "undefined" || !window.gtag) return; // no Ads configured
  const label = CONV_LABELS[key];
  if (!label) return; // no conversion label configured for this action
  const send_to = label.includes("/") ? label : `${ADS_ID}/${label}`;
  window.gtag("event", "conversion", { send_to, ...sanitize(params) });
}

/* ---------------- KLYROO event taxonomy (analytics events) ----------------
 * Commercial/lead events also invoke markConversion(...) which stays inert
 * until the matching Ads conversion ID + label are configured in env.
 */
export const track = {
  // Core analytics
  articleView: (a = {}) => trackEvent("article_view", {
    article_id: a.id, article_slug: a.slug, article_title: a.title,
    category: a.category, content_type: a.content_type, author: a.author,
    published_date: a.published_at,
  }),
  internalSearch: (term, count) => trackEvent("internal_search", { search_term: term, results_count: count }),
  scroll90: (path) => trackEvent("scroll_90", { page_path: path }),
  engagedSession: () => trackEvent("engaged_session", {}),

  // Commercial / value
  dealClick: (d = {}) => trackEvent("deal_click", {
    deal_id: d.id || d.slug, retailer: d.retailer, category: d.category,
    country: d.country, product_id: d.product_id || d.asin || d.sku,
    destination_type: d.affiliate_url ? "affiliate" : "retailer",
    page_location: window.location.href,
  }),
  affiliateClick: (p = {}) => trackEvent("affiliate_click", { ...p, page_location: window.location.href }),
  sponsoredAdClick: (ad = {}, placement) => trackEvent("sponsored_ad_click", {
    ad_id: ad.id || ad.slug, placement, advertiser: ad.advertiser || ad.name,
    page_location: window.location.href,
  }),
  outboundAdClick: (p = {}) => trackEvent("outbound_ad_click", { ...p, page_location: window.location.href }),

  // Lead / conversion (analytics event + optional Ads conversion)
  // Fire ONLY for a genuinely created record (caller passes the backend's new-record id).
  suggestionSubmit: (type, transactionId) => { trackEvent("suggestion_submit", { suggestion_type: type, transaction_id: transactionId }); markConversion("suggestion", { suggestion_type: type, transaction_id: transactionId }); },
  advertisingRequestSubmit: (placements, transactionId) => {
    trackEvent("advertising_request_submit", { placements_count: Array.isArray(placements) ? placements.length : undefined, transaction_id: transactionId });
    markConversion("advertising", { transaction_id: transactionId });
  },

  // Future-ready
  newsletterSignup: () => { trackEvent("newsletter_signup", {}); markConversion("newsletter", {}); },
  contentDownload: (p = {}) => trackEvent("content_download", p),
  videoStart: (p = {}) => trackEvent("video_start", p),
  videoComplete: (p = {}) => trackEvent("video_complete", p),
};


/* ---------------- Event taxonomy metadata (for the Admin health panel) ----------------
 * `bound` = there is a real UI trigger wired in the app today (Implemented YES).
 */
export const EVENT_TAXONOMY = [
  { name: "page_view", group: "Core", bound: true },
  { name: "article_view", group: "Core", bound: true },
  { name: "internal_search", group: "Core", bound: true },
  { name: "scroll_90", group: "Core", bound: true },
  { name: "engaged_session", group: "Core", bound: true },
  { name: "deal_click", group: "Commercial", bound: true },
  { name: "affiliate_click", group: "Commercial", bound: false },
  { name: "sponsored_ad_click", group: "Commercial", bound: true },
  { name: "outbound_ad_click", group: "Commercial", bound: false },
  { name: "suggestion_submit", group: "Conversion", bound: true },
  { name: "advertising_request_submit", group: "Conversion", bound: true },
  { name: "newsletter_signup", group: "Future", bound: false },
  { name: "content_download", group: "Future", bound: false },
  { name: "video_start", group: "Future", bound: false },
  { name: "video_complete", group: "Future", bound: false },
];

/* ---------------- First-party event-health beacon (no PII, no params) ----------------
 * Records only that a known event fired + when, to power the Admin health panel.
 * Independent of GA/consent. Never sends event parameters or user data.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HEALTH_NAMES = new Set(EVENT_TAXONOMY.map((e) => e.name));
function beaconHealth(name) {
  if (!BACKEND || typeof window === "undefined" || !HEALTH_NAMES.has(name)) return;
  try {
    fetch(`${BACKEND}/api/analytics/event-health`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* ignore */ }
}

/* ---------------- Consent storage (first-party, no PII) ----------------
 * Persists ONLY the visitor's category choices + version + timestamp in localStorage.
 * Maps to Google Consent Mode v2 via the existing updateConsent() (single source of truth).
 */
export const CONSENT_VERSION = 1;
const CONSENT_KEY = "klyroo_consent";

export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || c.version !== CONSENT_VERSION) return null;
    return c;
  } catch { return null; }
}

export function applyConsent(analytics, advertising) {
  initAnalytics(); // idempotent — ensures window.gtag exists before update
  updateConsent({
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: advertising ? "granted" : "denied",
    ad_user_data: advertising ? "granted" : "denied",
    ad_personalization: advertising ? "granted" : "denied",
  });
}

export function saveConsent(analytics, advertising) {
  const c = { version: CONSENT_VERSION, analytics: !!analytics, advertising: !!advertising, ts: new Date().toISOString() };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(c)); } catch { /* ignore */ }
  applyConsent(c.analytics, c.advertising);
  return c;
}

export function clearConsent() {
  try { localStorage.removeItem(CONSENT_KEY); } catch { /* ignore */ }
}

export function applyStoredConsent() {
  const c = getStoredConsent();
  if (!c) return null;
  applyConsent(c.analytics, c.advertising);
  return c;
}

/* Public config snapshot for the Admin health panel (no secrets — Measurement ID is public). */
export function getConfigStatus() {
  const dl = (typeof window !== "undefined" && window.dataLayer) || [];
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  return {
    measurementId: GA_ID || null,
    adsConversionId: ADS_ID || null,
    adsLabels: {
      advertising: !!CONV_LABELS.advertising,
      suggestion: !!CONV_LABELS.suggestion,
      newsletter: !!CONV_LABELS.newsletter,
    },
    environment: /(^|\.)preview\.|localhost|127\.0\.0\.1/.test(host) ? "Preview" : "Production",
    tagDetected: typeof window !== "undefined" && typeof window.gtag === "function" && Array.isArray(window.dataLayer),
    configExecuted: dl.some((a) => a[0] === "config" && a[1] === GA_ID),
    consentDefault: { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" },
  };
}
